import {
  PUSH_NOTIFICATION_HEARTBEAT_INTERVAL_MS,
  PushNotificationConnectionStatus,
  PushNotificationSignalRMethod,
} from 'constants/pushNotifications';

import {
  fetchNotificationsAction,
  getPushNotificationsStatisticsAction,
  notificationReceivedAction,
  setConnectionStatusAction,
} from 'store/pushNotifications/actions';

class SignalRPushNotificationsController {
  #connection;
  #dispatch;
  #logger;
  #heartbeatInterval;
  #userId;
  #companyId;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use push notifications connection without active connection');
    }

    return this.#connection;
  }

  init(dispatch) {
    if (!dispatch) {
      this.#logger.error(
        'Dispatch is required for initializing push notifications signalR controller',
      );
    }

    this.#dispatch = dispatch;
  }

  start(userId, companyId) {
    this.#userId = userId;
    this.#companyId = companyId;
    this.#connection.invoke(PushNotificationSignalRMethod.CONNECT, companyId);
    this.#startListeners();
    this.#startHeartbeat();
    this.#dispatch(setConnectionStatusAction(PushNotificationConnectionStatus.CONNECTED));
    this.#dispatch(getPushNotificationsStatisticsAction({ userId }));
  }

  stop() {
    this.#stopListeners();
    this.#stopHeartbeat();

    if (this.#companyId) {
      this.#connection.invoke(PushNotificationSignalRMethod.DISCONNECT, this.#companyId);
    }

    this.#userId = null;
    this.#companyId = null;
    this.#dispatch(setConnectionStatusAction(PushNotificationConnectionStatus.DISCONNECTED));
  }

  #startListeners() {
    this.connection.on(
      PushNotificationSignalRMethod.NOTIFICATION_CREATED,
      this.#onNotificationCreated.bind(this),
    );

    this.connection.onreconnecting(this.#onReconnecting.bind(this));
    this.connection.onreconnected(this.#onReconnected.bind(this));
    this.connection.onclose(this.#onClose.bind(this));
  }

  #stopListeners() {
    this.connection.off(PushNotificationSignalRMethod.NOTIFICATION_CREATED);
  }

  #startHeartbeat() {
    this.#stopHeartbeat();

    this.#heartbeatInterval = setInterval(() => {
      if (!this.#connection.isConnected) {
        return;
      }

      this.#connection.invoke(PushNotificationSignalRMethod.HEARTBEAT, this.#companyId);
    }, PUSH_NOTIFICATION_HEARTBEAT_INTERVAL_MS);
  }

  #stopHeartbeat() {
    if (this.#heartbeatInterval) {
      clearInterval(this.#heartbeatInterval);
      this.#heartbeatInterval = null;
    }
  }

  #onNotificationCreated(notification) {
    this.#dispatch(notificationReceivedAction(notification));
  }

  #onReconnecting() {
    this.#dispatch(setConnectionStatusAction(PushNotificationConnectionStatus.RECONNECTING));
  }

  #onReconnected() {
    this.#dispatch(setConnectionStatusAction(PushNotificationConnectionStatus.CONNECTED));
    this.#startHeartbeat();

    if (this.#userId) {
      this.#dispatch(getPushNotificationsStatisticsAction({ userId: this.#userId }));
      this.#dispatch(
        fetchNotificationsAction({
          userId: this.#userId,
        }),
      );
    }
  }

  #onClose() {
    this.#stopHeartbeat();
    this.#dispatch(setConnectionStatusAction(PushNotificationConnectionStatus.DISCONNECTED));
  }
}

export default SignalRPushNotificationsController;
